import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';
import { Capacitor } from '@capacitor/core';

/**
 * Haptic Feedback Helper for Native Apps
 * Provides consistent haptic feedback across the app
 */

class HapticFeedbackService {
    private isNative: boolean;

    constructor() {
        this.isNative = Capacitor.isNativePlatform();
    }

    /**
     * Light impact - for subtle interactions
     * Use for: list item selection, tab switching, minor toggles
     */
    async light() {
        if (!this.isNative) return;
        try {
            await Haptics.impact({ style: ImpactStyle.Light });
        } catch (error) {
            console.warn('Haptic feedback not available:', error);
        }
    }

    /**
     * Medium impact - for standard interactions
     * Use for: button clicks, form submissions, swipe actions
     */
    async medium() {
        if (!this.isNative) return;
        try {
            await Haptics.impact({ style: ImpactStyle.Medium });
        } catch (error) {
            console.warn('Haptic feedback not available:', error);
        }
    }

    /**
     * Heavy impact - for important interactions
     * Use for: delete actions, error states, major confirmations
     */
    async heavy() {
        if (!this.isNative) return;
        try {
            await Haptics.impact({ style: ImpactStyle.Heavy });
        } catch (error) {
            console.warn('Haptic feedback not available:', error);
        }
    }

    /**
     * Success notification
     * Use for: successful form submission, post created, message sent
     */
    async success() {
        if (!this.isNative) return;
        try {
            await Haptics.notification({ type: NotificationType.Success });
        } catch (error) {
            console.warn('Haptic feedback not available:', error);
        }
    }

    /**
     * Warning notification
     * Use for: validation errors, warnings, attention needed
     */
    async warning() {
        if (!this.isNative) return;
        try {
            await Haptics.notification({ type: NotificationType.Warning });
        } catch (error) {
            console.warn('Haptic feedback not available:', error);
        }
    }

    /**
     * Error notification
     * Use for: failed actions, critical errors, deletion confirmations
     */
    async error() {
        if (!this.isNative) return;
        try {
            await Haptics.notification({ type: NotificationType.Error });
        } catch (error) {
            console.warn('Haptic feedback not available:', error);
        }
    }

    /**
     * Selection changed - for picker/selector changes
     */
    async selectionChanged() {
        if (!this.isNative) return;
        try {
            await Haptics.selectionChanged();
        } catch (error) {
            console.warn('Haptic feedback not available:', error);
        }
    }

    /**
     * Vibrate with pattern (Android)
     * Use for custom vibration patterns
     */
    async vibrate(duration: number = 300) {
        if (!this.isNative) return;
        try {
            await Haptics.vibrate({ duration });
        } catch (error) {
            console.warn('Haptic feedback not available:', error);
        }
    }
}

// Export singleton instance
export const HapticFeedback = new HapticFeedbackService();

// Export class for testing or custom instances
export default HapticFeedbackService;
